import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { TicketStatusBadge } from "@/components/TicketStatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { FileText, LifeBuoy, Loader2, MessageSquarePlus, Send, ShieldCheck } from "lucide-react";

const CATEGORIES = [
  { value: "orders", label: "Report orders" },
  { value: "billing", label: "Billing & membership" },
  { value: "technical", label: "Technical issue" },
  { value: "general", label: "General question" },
];

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

export default function SupportPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [linkedOrderId, setLinkedOrderId] = useState("");
  const [creating, setCreating] = useState(false);
  const threadEndRef = useRef(null);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets");
      setTickets(data.tickets);
      return data.tickets;
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load your tickets."));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const openThread = useCallback(async (ticketId) => {
    setSelectedId(ticketId);
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setThread(data);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load the conversation."));
    } finally {
      setThreadLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets().then((rows) => {
      if (rows.length > 0) openThread(rows[0].id);
    });
  }, [loadTickets, openThread]);

  // Deep link from an order row: /support?order=<id> pre-fills the new-ticket dialog.
  useEffect(() => {
    const orderParam = searchParams.get("order");
    if (orderParam) {
      setLinkedOrderId(orderParam);
      setCategory("orders");
      setSubject(`Help with order ${orderParam.slice(0, 8)}`);
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const createTicket = async () => {
    setCreating(true);
    try {
      const payload = { subject, category, message };
      if (linkedOrderId) payload.orderId = linkedOrderId;
      const { data } = await api.post("/tickets", payload);
      toast.success("Ticket created. Our team has been notified.");
      setDialogOpen(false);
      setSubject("");
      setCategory("general");
      setMessage("");
      setLinkedOrderId("");
      await loadTickets();
      openThread(data.ticket.id);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not create the ticket."));
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    if (!selectedId || reply.trim().length < 2) return;
    setSending(true);
    try {
      await api.post(`/tickets/${selectedId}/messages`, { body: reply });
      setReply("");
      await openThread(selectedId);
      loadTickets();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Reply failed to send."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2.5">
              <LifeBuoy className="h-6 w-6 text-primary" /> Support center
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Open a ticket about an order, billing, or anything else. Our team replies here and by email.
            </p>
          </div>
          <Button data-testid="support-new-ticket-button" onClick={() => setDialogOpen(true)}>
            <MessageSquarePlus className="h-4 w-4 mr-1.5" /> New ticket
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          {/* Ticket list */}
          <Card className="bg-card border-border h-fit">
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-lg">Your tickets</CardTitle>
              <CardDescription>{tickets.length} conversation{tickets.length === 1 ? "" : "s"}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <>
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </>
              ) : tickets.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4" data-testid="support-empty-state">
                  No tickets yet. Create one and our operations team will get back to you.
                </p>
              ) : (
                <ScrollArea className="max-h-[480px]">
                  <div className="space-y-2 pr-2">
                    {tickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        type="button"
                        data-testid={`support-ticket-item-${ticket.id}`}
                        onClick={() => openThread(ticket.id)}
                        className={`w-full rounded-md border p-3 text-left transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                          selectedId === ticket.id ? "border-primary/50 bg-secondary/70" : "border-border bg-background"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium truncate">{ticket.subject}</p>
                          <TicketStatusBadge status={ticket.status} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 capitalize">
                          {ticket.category} · {formatWhen(ticket.updated_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* Thread */}
          <Card className="bg-card border-border flex flex-col">
            {!selectedId ? (
              <CardContent className="flex flex-1 items-center justify-center py-16">
                <p className="text-sm text-muted-foreground" data-testid="support-thread-placeholder">
                  Select a ticket to view the conversation.
                </p>
              </CardContent>
            ) : threadLoading || !thread ? (
              <CardContent className="space-y-3 py-6">
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-3/4" />
              </CardContent>
            ) : (
              <>
                <CardHeader className="pb-3 border-b border-border">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="font-display text-lg" data-testid="support-thread-subject">
                        {thread.ticket.subject}
                      </CardTitle>
                      <CardDescription className="capitalize">
                        {thread.ticket.category} · opened {formatWhen(thread.ticket.created_at)}
                      </CardDescription>
                    </div>
                    <TicketStatusBadge status={thread.ticket.status} />
                  </div>
                  {thread.order_summary && (
                    <div
                      className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-3 py-2"
                      data-testid="support-linked-order-chip"
                    >
                      <FileText className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-medium">
                        Linked order <span className="font-mono">{thread.order_summary.id.slice(0, 8)}</span>
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">
                        {String(thread.order_summary.request_type || "").replaceAll("_", " ")} ·{" "}
                        <span className="font-mono">{thread.order_summary.identifier_value}</span> ·{" "}
                        {String(thread.order_summary.verification_status || "").replaceAll("_", " ")} ·{" "}
                        {thread.order_summary.report_paid ? "paid" : "unpaid"}
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="flex-1 pt-4">
                  <ScrollArea className="max-h-[420px]">
                    <div className="space-y-3 pr-2" data-testid="support-thread-messages">
                      {thread.messages.map((msg) => {
                        const mine = msg.author_id === user?.id;
                        const fromAdmin = msg.author_role === "admin";
                        return (
                          <div key={msg.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[85%] rounded-md border p-3 ${
                                fromAdmin
                                  ? "border-primary/30 bg-primary/10"
                                  : "border-border bg-secondary/60"
                              }`}
                            >
                              <p className="text-[11px] text-muted-foreground mb-1 flex items-center gap-1">
                                {fromAdmin && <ShieldCheck className="h-3 w-3 text-primary" />}
                                {fromAdmin ? "Support team" : "You"} · {formatWhen(msg.created_at)}
                              </p>
                              <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={threadEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>
                <div className="border-t border-border p-4 flex items-end gap-2">
                  <Textarea
                    data-testid="support-reply-input"
                    placeholder={
                      thread.ticket.status === "closed"
                        ? "This ticket is closed. Replying will reopen it."
                        : "Write a reply…"
                    }
                    className="min-h-[64px] resize-none"
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                  />
                  <Button
                    data-testid="support-reply-send-button"
                    onClick={sendReply}
                    disabled={sending || reply.trim().length < 2}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">New support ticket</DialogTitle>
            <DialogDescription>
              Describe the issue and our operations team will respond here and by email.
            </DialogDescription>
          </DialogHeader>
          {linkedOrderId && (
            <div
              className="flex items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-3 py-2"
              data-testid="support-dialog-linked-order"
            >
              <FileText className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs">
                This ticket will be linked to order{" "}
                <span className="font-mono font-medium">{linkedOrderId.slice(0, 8)}</span> so our team has the
                full context.
              </span>
            </div>
          )}
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="ticket-subject">Subject</Label>
              <Input
                id="ticket-subject"
                data-testid="support-subject-input"
                placeholder="e.g. Question about my report order"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="support-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ticket-message">Message</Label>
              <Textarea
                id="ticket-message"
                data-testid="support-message-input"
                className="min-h-[110px]"
                placeholder="Give us as much detail as you can, including any order ID."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              data-testid="support-cancel-button"
              onClick={() => {
                setDialogOpen(false);
                setLinkedOrderId("");
              }}
            >
              Cancel
            </Button>
            <Button
              data-testid="support-submit-ticket-button"
              onClick={createTicket}
              disabled={creating || subject.trim().length < 3 || message.trim().length < 5}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
              Submit ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SiteFooter />
    </div>
  );
}
